import { type Component, createMemo } from "solid-js";
import { getRequestEvent, isServer } from "solid-js/web";
import { twMerge } from "tailwind-merge";
import { useKeyboardShortcutSuggestions } from "~/hooks/useKeyboardShortcutSuggestions";

const isMac = () => {
  if (isServer) {
    const event = getRequestEvent();
    return event?.request.headers.get("User-Agent")?.includes("Mac") !== false;
  }
  return window.navigator.userAgent.includes("Mac");
};

const normalizeKey = (key: string): string => {
  const lower = key.toLowerCase();
  if (["cmd", "command", "ctrl", "control"].includes(lower)) {
    return isMac() ? "⌘" : "Ctrl";
  }
  if (lower === "shift") {
    return "⇧";
  }
  if (["alt", "opt", "option"].includes(lower)) {
    return isMac() ? "⌥" : "Alt";
  }
  if (lower === "up" || lower === "arrowup") {
    return "↑";
  }
  if (lower === "down" || lower === "arrowdown") {
    return "↓";
  }
  if (lower === "left" || lower === "arrowleft") {
    return "←";
  }
  if (lower === "right" || lower === "arrowright") {
    return "→";
  }
  if (lower === "escape" || lower === "esc") {
    return "Esc";
  }
  // If the key is already an arrow symbol, return it as-is
  if (key === "↑" || key === "↓" || key === "←" || key === "→") {
    return key;
  }
  return key.toUpperCase();
};

/**
 * Component that displays a keyboard shortcut in a pill box inline.
 * Hidden by default, shown on hover of the parent element.
 * Place this inside an element and use group/group-hover classes on the parent.
 */
export const KeyboardShortcutInlinePill: Component<{
  keys: string[];
  class?: string;
  groupName?: string;
  /** When true, show at full opacity without requiring parent group-hover. */
  alwaysVisible?: boolean;
}> = (props) => {
  const { showShortcutHints } = useKeyboardShortcutSuggestions();
  const displayKeys = createMemo(() => props.keys.map(normalizeKey).join(" "));
  // Use scoped group hover if groupName is provided, otherwise use default group-hover
  // Only apply hover class when hints are shown
  const groupHoverClass = createMemo(() => {
    if (!showShortcutHints() || props.alwaysVisible) return "";
    if (props.groupName) {
      return `group-hover/${props.groupName}:opacity-100 group-focus-visible/${props.groupName}:opacity-100`;
    }
    return "group-hover:opacity-100 group-focus-visible:opacity-100";
  });

  // Hidden unless Show; hover-reveal only when Show and not alwaysVisible
  return (
    <span
      class={twMerge(
        "inline-flex items-center rounded-md border border-gray-300 bg-gray-50 px-2 py-0.5 text-xs whitespace-nowrap text-gray-700 transition-opacity duration-150 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300",
        showShortcutHints() ? (props.alwaysVisible ? "opacity-100" : "opacity-0") : "hidden",
        groupHoverClass(),
        props.class
      )}
    >
      {displayKeys()}
    </span>
  );
};
